/**
 * Reglas de los bonos (boletos gratis) — espejo de las validaciones del backend
 * (create_bet). Ver docs/api/BONOS_REFERIDOS_PROMOS.md del backend.
 *
 * - FRACTION ("1 fracción gratis") → solo en una apuesta de exactamente 1 fracción.
 * - TICKET ("1 billete gratis") → solo en una apuesta del billete completo
 *   (fracciones == fraction_count de la lotería).
 * - Un bono por apuesta y un mismo bono no se puede usar en dos apuestas.
 * - `all_lotteries=false` limita las loterías a `lotteries: [{ id, name }]`.
 */

export const BONUS_KIND = {
  FRACTION: "FRACTION",
  TICKET: "TICKET",
};

const normalizeName = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

export function itemFractions(item) {
  return Number.parseInt(String(item?.cantidad ?? item?.fracciones ?? 1), 10) || 1;
}

/** Fracciones del billete completo de la lotería del ítem (fraction_count). */
export function itemTicketFractions(item) {
  const parsed = Number.parseInt(String(item?.maxFracciones ?? item?.number_fractions ?? ""), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function itemLotteryName(item) {
  return item?.loteria || item?.lottery || "";
}

/** ¿El bono vale para la lotería del ítem? */
export function bonusAllowsLottery(bonus, item) {
  if (!bonus) return false;
  if (bonus.all_lotteries !== false) return true;
  const lotteries = Array.isArray(bonus.lotteries) ? bonus.lotteries : [];
  const name = normalizeName(itemLotteryName(item));
  return lotteries.some(
    (lot) =>
      (lot?.id && item?.lotteryId && String(lot.id) === String(item.lotteryId)) ||
      (lot?.name && normalizeName(lot.name) === name)
  );
}

/** ¿La cantidad de fracciones del ítem cumple la regla del tipo de bono? */
export function bonusFitsFractions(bonus, item) {
  const qty = itemFractions(item);
  if (bonus?.kind === BONUS_KIND.FRACTION) return qty === 1;
  if (bonus?.kind === BONUS_KIND.TICKET) {
    const full = itemTicketFractions(item);
    return full != null && qty === full;
  }
  return false;
}

export function isBonusEligibleForItem(bonus, item) {
  return bonusAllowsLottery(bonus, item) && bonusFitsFractions(bonus, item);
}

/** Fracciones que necesita el ítem para usar el bono (null si no aplica). */
export function fractionsNeededForBonus(bonus, item) {
  if (bonus?.kind === BONUS_KIND.FRACTION) return 1;
  if (bonus?.kind === BONUS_KIND.TICKET) return itemTicketFractions(item);
  return null;
}

const byExpiry = (a, b) =>
  new Date(a?.expires_at || 0).getTime() - new Date(b?.expires_at || 0).getTime();

/**
 * Asigna bonos a los ítems del carrito (un bono por ítem, sin repetir).
 * 1) Respeta las elecciones explícitas del usuario que sigan siendo válidas.
 * 2) A los ítems sin elección, les sugiere el bono compatible que vence primero.
 *
 * @param {object[]} items  Ítems del carrito
 * @param {object[]} bonuses Bonos disponibles
 * @param {Record<string, string>} choices  itemId → bonusId | "balance"
 * @param {(item: object) => boolean} [skipItem]  Ítems que no deben recibir bono
 * @returns {Record<string, string>} itemId → bonusId
 */
export function assignBonuses(items, bonuses, choices = {}, skipItem = () => false) {
  const sorted = [...(bonuses || [])].sort(byExpiry);
  const used = new Set();
  const result = {};

  items.forEach((item) => {
    const choice = choices[item.id];
    if (!choice || choice === "balance" || skipItem(item)) return;
    const bonus = sorted.find((b) => b.id === choice);
    if (bonus && !used.has(bonus.id) && isBonusEligibleForItem(bonus, item)) {
      result[item.id] = bonus.id;
      used.add(bonus.id);
    }
  });

  items.forEach((item) => {
    if (choices[item.id] !== undefined || result[item.id] || skipItem(item)) return;
    const bonus = sorted.find((b) => !used.has(b.id) && isBonusEligibleForItem(b, item));
    if (bonus) {
      result[item.id] = bonus.id;
      used.add(bonus.id);
    }
  });

  return result;
}

/** "dd/mm" en hora de Colombia. */
export function formatBonusShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Bogota",
  });
}

/** "1 de noviembre de 2026" en hora de Colombia. */
export function formatBonusLongDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "America/Bogota",
  });
}

/** "Vence hoy" / "Vence mañana" / "Quedan 12 días". */
export function bonusDaysLeftText(bonus) {
  const days = Number(bonus?.days_left);
  if (!Number.isFinite(days)) return "";
  if (days <= 0) return "Vence hoy";
  if (days === 1) return "Vence mañana";
  return `Quedan ${days} días`;
}

/** Texto de las loterías en que sirve el bono. */
export function bonusLotteriesText(bonus) {
  if (!bonus || bonus.all_lotteries !== false) return "Válido en todas las loterías";
  const names = (Array.isArray(bonus.lotteries) ? bonus.lotteries : [])
    .map((l) => l?.name)
    .filter(Boolean);
  return names.length ? `Válido en: ${names.join(", ")}` : "Válido en loterías seleccionadas";
}
